import { Anchor, CalendarClock, Wallet, X, Play } from "lucide-react";
import { useI18n } from "@/i18n";
import { Reveal, SectionHead } from "@/components/motion";
import { img } from "@/lib/gallery";

const Card = ({ Icon, title, children, testid }) => (
  <Reveal className="flex flex-col border border-white/12 bg-white/[0.03] p-7" data-testid={testid}>
    <Icon className="h-7 w-7 text-[var(--gold)]" strokeWidth={1.4} />
    <p className="font-display mt-6 text-2xl font-bold uppercase leading-tight">{title}</p>
    <div className="mt-3 text-sm leading-relaxed text-[var(--steel)]">{children}</div>
  </Reveal>
);

const VideoBlock = () => {
  const { t } = useI18n();
  const v = t.video;
  return (
    <div className="mt-24 grid gap-12 lg:grid-cols-12 lg:items-center" data-testid="video-section">
      <div className="group relative aspect-video overflow-hidden lg:col-span-7">
        <img src={img(18)} alt={v.title} className="h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
        <div className="absolute inset-0 bg-[#070C1A]/35" />
        <span className="absolute left-5 top-5 flex items-center gap-2 bg-[#070C1A]/80 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em]">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />REC · FAT
        </span>
        <span className="absolute inset-0 m-auto flex h-20 w-20 items-center justify-center rounded-full border border-white/60 bg-white/10 backdrop-blur-md">
          <Play className="ml-1 h-7 w-7 fill-white text-white" />
        </span>
        <span className="absolute bottom-5 left-5 right-5 font-mono text-[11px] uppercase tracking-[0.14em] text-white">{v.badge}</span>
      </div>
      <div className="lg:col-span-5">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--ember)]">{v.eyebrow}</p>
        <h3 className="font-display mt-4 text-4xl font-extrabold uppercase leading-[0.95] sm:text-5xl" data-testid="video-title">{v.title}</h3>
        <p className="mt-5 text-[var(--steel)]">{v.text}</p>
        <ul className="mt-6 grid grid-cols-1 gap-x-6 sm:grid-cols-2">
          {v.items.map((x) => (
            <li key={x} className="flex items-center gap-3 border-t border-white/10 py-2.5 text-sm">
              <span className="h-1.5 w-1.5 rotate-45 bg-[var(--gold)]" />{x}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const Delivery = () => {
  const { t } = useI18n();
  const d = t.delivery;
  return (
    <section id="livraison" className="bg-[var(--night)] py-24 lg:py-32" data-testid="delivery-section">
      <div className="container-x">
        <SectionHead index="06" eyebrow={d.eyebrow} title={d.title} />
        <div className="mt-14 grid gap-4 lg:grid-cols-3">
          <Card Icon={Anchor} title={d.portT} testid="delivery-port"><p>{d.portD}</p></Card>
          <Card Icon={CalendarClock} title={d.leadT} testid="delivery-lead">
            <p>{d.leadD}</p>
            <ul className="mt-2 space-y-1">{d.leadFrom.map((x) => <li key={x}>— {x}</li>)}</ul>
          </Card>
          <Card Icon={Wallet} title={d.payT} testid="delivery-payment">
            <div className="mb-4 flex h-2.5 overflow-hidden"><span className="w-[30%] bg-[var(--gold)]" /><span className="w-[70%] bg-[var(--steel-dim)]/50" /></div>
            <p>{d.payD}</p>
          </Card>
        </div>
        <Reveal className="mt-4 border border-white/12 p-7" data-testid="delivery-exclusions">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">{d.exclT}</p>
          <ul className="mt-5 grid gap-x-8 gap-y-3 text-sm text-[var(--steel)] sm:grid-cols-2 lg:grid-cols-5">
            {d.excl.map((x) => <li key={x} className="flex gap-2"><X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--steel-dim)]" />{x}</li>)}
          </ul>
        </Reveal>
        <VideoBlock />
      </div>
    </section>
  );
};
